import { useMemo, useCallback } from 'react';

import { Stack, Button, Tooltip, MenuItem, TextField, InputAdornment } from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { showCompanyEditor } from 'src/store/slices/companies/companiesSlice';

import Iconify from 'src/components/iconify';
import { TableProps } from 'src/components/table';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { TenantType } from 'src/types/enums';
import TableSorter from 'src/components/sorter/table-sorter';
import { SortOrder } from 'src/services/common.types';

export interface ToolbarOptions {
  addButton: boolean;
}

export type Props = {
  table?: TableProps;
  options?: ToolbarOptions | undefined;
  onFilters?: (value: string) => void;
  onAddToList?: (companyIds: string[]) => void;
  onSortChange?: (sortConfig: { sort_by: string; sort_order: SortOrder }) => void;
  onBulkImport?: () => void;
};

export default function CompaniesTableToolbar({
  options = { addButton: true },
  table,
  onFilters,
  onAddToList,
  onSortChange,
  onBulkImport,
}: Props) {

  const { t } = useTranslate();

  const dispatch = useAppDispatch();

  const tenantType = useOrgTenant();

  const actionsControl = usePopover();

  const handleFilterName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onFilters ? onFilters(event.target.value) : null,
    [onFilters]
  );

  const onAddCompany = useCallback(() => {
    dispatch(showCompanyEditor(undefined));
  }, [dispatch]);

  const showActions = useMemo(() => ([TenantType.Recruiter, TenantType.Client].includes(tenantType) && options.addButton) || onAddToList, [tenantType, options.addButton, onAddToList]);

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{
        xs: 'column',
        sm: 'row',
      }}
      sx={{
        p: 2.5,
        pr: { xs: 2.5, md: 1 },
        width: '100%',
      }}
    >
      {onFilters && (
        <TextField
          onChange={handleFilterName}
          placeholder="Search..."
          name="description"
          sx={{
            minWidth: {
              xs: '100%',
              sm: '360px',
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      )}
      <Stack
        sx={{ width: '100%' }}
        direction="row"
        justifyContent="flex-end"
        spacing={2}
        flexGrow={1}
      >
        {
          onSortChange && (
            <TableSorter
              sortOptions={[
                { key: 'company_name', label: t('companies.sorters.company_name') },
                { key: 'created_at', label: t('companies.sorters.created_at') },
                { key: 'updated_at', label: t('companies.sorters.updated_at') }
              ]}
              onChange={(sort) => onSortChange?.(sort)}
            />
          )
        }

        {showActions && (
          <Button
            size="medium"
            onClick={actionsControl.onOpen}
            startIcon={<Iconify icon="solar:hamburger-menu-line-duotone" />}
            variant="outlined"
            sx={{ textWrap: 'nowrap', width: { xs: '100%', sm: 'unset' } }}
          >
            {t('common.actions')}
          </Button>
        )}

      </Stack>

      <CustomPopover
        open={actionsControl.open}
        onClose={actionsControl.onClose}
        arrow="top-right"
      >
        <Stack>
          {[TenantType.Recruiter, TenantType.Client].includes(tenantType) && (
            <MenuItem
              onClick={() => {
                actionsControl.onClose();
                onAddCompany?.();
              }}
            >
              <Iconify icon="material-symbols:add" />
              {t(`companies.actions.add_company`)}
            </MenuItem>
          )}

          {
            onAddToList && (
              <Tooltip title={!table?.selected.length ? t('lists.bulk_add_disabled_tootip') : ''}>
                <span>
                  <MenuItem
                    disabled={!table?.selected.length}
                    onClick={() => {
                      actionsControl.onClose();
                      onAddToList?.(table?.selected ?? []);
                    }}
                  >
                    <Iconify icon="material-symbols:contextual-token-add-outline" />
                    {t('lists.add_to_list')}
                  </MenuItem>
                </span>
              </Tooltip>
            )
          }

          {onBulkImport && (
            <MenuItem
              onClick={() => {
                actionsControl.onClose();
                onBulkImport?.();
              }}
            >
              <Iconify icon="stash:file-import-duotone" />
              {t(`companies.actions.bulk_import`)}
            </MenuItem>
          )}
        </Stack>
      </CustomPopover >

    </Stack >
  );
}
